import React from 'react';
import uniqid from 'uniqid';

import { Box } from 'ui/box';

import { Loader } from 'ui/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Accordion from 'components/partials/accordion/accordion';
import { Typography } from 'ui/typography';

import useFetchCourse from 'containers/user/learn/containers/content/cources/use-fetch-course';
import CourseControlButtons from 'containers/user/learn/containers/content/cources/course-controls/course-controls';
import ProgressBar from 'ui/progress-bar';
import Progress from 'containers/user/learn/components/progress/progress';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';
import { COURSE_STATUSES } from 'containers/user/learn/containers/content/cources/course.constants';

interface CourseOverviewProps {
  courseId: string | number;
}

const CourseOverview = ({
  courseId,
}: CourseOverviewProps): React.JSX.Element => {
  const user = useCurrentUser();

  const { loading, course } = useFetchCourse(user?.data?.oktaUserId, courseId);

  const isNew = course && course.status === COURSE_STATUSES.UNKNOWN;

  if (!loading && !course) {
    return (
      <Box sx={{ my: 'auto' }}>
        <p className="text-4xl">
          Course data is not available at the moment. Please try again.
        </p>
      </Box>
    );
  }

  return loading ? (
    <Loader {...{ loading }} />
  ) : (
    <ResponsiveWrapper customClass="flex flex-col">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Learn', href: '/learn' },
          { label: 'Courses', href: '/learn/courses' },
        ]}
        customClass="!mt-5"
      />
      <Typography
        data-testid="course-overview-heading"
        variant="h1"
        sx={{ pt: { mobile: 2, xs: 3 }, mb: 5 }}
      >
        {course!.name}
      </Typography>

      <ProgressBar
        value={isNew ? 0 : course!.averageProgress}
        aria-label="Course progress"
        sx={{ mb: 1.5 }}
      />
      {!isNew && <Progress averageProgress={course!.averageProgress} />}

      <Box
        sx={{
          width: '100%',
          '@media (min-width: 375px)': { my: 2.5 },
          '@media (min-width: 960px)': { my: 5 },
        }}
      >
        {course!.content?.map((component) =>
          getBlocksFromContent({ ...component, key: uniqid() })
        )}
      </Box>
      <Typography
        data-testid="course-lessons-heading"
        variant="h2"
        sx={{ mb: 1.5, color: 'tokens.gray.950' }}
      >
        Lessons
      </Typography>

      <Accordion
        customClass="mobile:mb-5 xs:mb-10"
        items={
          course!.lessons?.data.map((item) => ({
            name: item.attributes.name ?? '',
            description: item.attributes.shortDescription ?? '',
          })) ?? []
        }
      />

      <CourseControlButtons
        userId={user?.data?.oktaUserId}
        course={course!}
        size="md"
      />
    </ResponsiveWrapper>
  );
};

export default CourseOverview;
